// @flow

import type { Dispatch } from 'redux';
import {
  getTermFilters,
  getAssortment,
  getRangeFilters,
  getFilteredAssortment,
  getSortProperty,
  getSortDirection,
  getCustomFilters,
  hasFilters,
  getSortType,
} from '../selectors';
import { applyFiltering } from '../helpers';
import { selectorByPdbProperty } from '../config';
import compare, { type SortDirection, type SortType } from '../../../helpers/compare';
import type { GlobalState } from '../types';
import { RECEIVE_ASSORTMENT, RECEIVE_FILTERED_ASSORTMENT } from '../constants';
import { updateFacetsNumbers } from './facets';
import reflectFiltersInUrl from './filter/reflectFiltersInUrl';

export type ReceiveAssortment = {|
  type: typeof RECEIVE_ASSORTMENT,
  payload: Array<string>,
|};

const receiveAssortment = (ids: Array<string>): ReceiveAssortment => ({
  type: RECEIVE_ASSORTMENT,
  payload: ids,
});

export type ReceiveFilteredAssortment = {|
  type: typeof RECEIVE_FILTERED_ASSORTMENT,
  payload: Array<string>,
|};

const receiveFilteredAssortment = (ids: Array<string>): ReceiveFilteredAssortment => ({
  type: RECEIVE_FILTERED_ASSORTMENT,
  payload: ids,
});

export const sortAssortment = (
  state: GlobalState,
  assortment: Array<string>,
  pdbProperty: string,
  direction: SortDirection = 'asc',
  type: SortType = 'string',
): Array<string> => [...assortment]
  .sort((pdbA, pdbB) => {
    const selector = selectorByPdbProperty[pdbProperty];
    let valueA = selector(state, pdbA);
    let valueB = selector(state, pdbB);

    if (type === 'number') {
      valueA = parseFloat(valueA);
      valueB = parseFloat(valueB);
    }

    return compare(
      valueA,
      valueB,
      direction,
    );
  });

export const filterAssortment = (selectedFacet?: string): Function => (
  (dispatch: Dispatch<any>, getState: () => GlobalState): void => {
    const state = getState();
    const assortment = getAssortment(state);

    // apply filtering on the assortment
    let filteredAssortment = assortment;
    if (hasFilters(state)) {
      filteredAssortment = applyFiltering(
        state,
        assortment,
        getTermFilters(state),
        getRangeFilters(state),
        getCustomFilters(state),
      );
    }
    // apply sorting
    const sortProperty = getSortProperty(state);
    if (sortProperty) {
      filteredAssortment = sortAssortment(
        state,
        filteredAssortment,
        sortProperty,
        getSortDirection(state),
        getSortType(state),
      );
    }

    // regenerate facets, update sort and apply filtering
    dispatch(reflectFiltersInUrl());
    dispatch(receiveFilteredAssortment(filteredAssortment));
    dispatch(updateFacetsNumbers(selectedFacet));
  }
);

export const updateAssortmentSort = () => (
  (dispatch: Dispatch<any>, getState: () => GlobalState): void => {
    const state = getState();
    const sortProperty = getSortProperty(state);
    if (sortProperty) {
      const sorted = sortAssortment(
        state,
        getFilteredAssortment(state),
        sortProperty,
        getSortDirection(state),
        getSortType(state),
      );
      dispatch(receiveFilteredAssortment(sorted));
      dispatch(reflectFiltersInUrl());
    }
  }
);

export default receiveAssortment;
